import {
  CheckCircleFilled,
  CloseCircleFilled,
  CopyOutlined,
  EyeOutlined,
  LinkOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Empty,
  List,
  message,
  Modal,
  Progress,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd'
import copy from 'copy-to-clipboard'
import { useCallback, useMemo } from 'react'

import { useVestingContextV2 } from 'context/vesting-context-provider-v2'
import { useWeb3ModalHook } from 'hooks/use-web3-modal-hook'
import { useImportVestingMultisig } from 'hooks/vesting/use-import-vesting-multisig'
import { formatAddress } from 'utils/contract'

const BATCH_SIZE = 250
const MODAL_WIDTH = 600
const IMPORT_BUTTON_WIDTH = 300
const HASH_DISPLAY_CHARS = 10

type ModalMode = 'viewing' | 'proposing' | 'done' | 'error' | null

interface BatchListItem {
  safeTxHash: string
  safeWebUrl: string
  entriesCount: number
  batchIndex?: number
  nonce?: number
  confirmations?: number
  confirmationsRequired?: number
}

const MODAL_TITLES: Record<Exclude<ModalMode, null | 'proposing'>, string> = {
  viewing: 'Pending Vesting Import Batches',
  done: 'Submitted to Safe Multisig',
  error: 'Failed to submit to Safe',
}

const copyToClipboard = (text: string) => {
  copy(text)
  message.success('Copied to clipboard')
}

const BatchList = ({ items }: { items: BatchListItem[] }) => (
  <List
    size="small"
    bordered
    dataSource={items}
    style={{ marginBottom: 16 }}
    renderItem={(item) => {
      const label =
        item.batchIndex !== undefined ? `Batch #${item.batchIndex + 1}` : `Nonce #${item.nonce}`
      const showSignatures = item.confirmationsRequired !== undefined
      const isFullySigned =
        showSignatures && (item.confirmations ?? 0) >= (item.confirmationsRequired ?? 0)

      return (
        <List.Item
          actions={[
            <Button
              key="copy"
              type="text"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(item.safeWebUrl)}
            >
              Copy
            </Button>,
            <Button
              key="open"
              type="text"
              icon={<LinkOutlined />}
              href={item.safeWebUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open
            </Button>,
          ]}
        >
          <Space direction="vertical" size={0}>
            <Typography.Text strong>
              {label}
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {item.entriesCount} entries
              </Tag>
              {showSignatures && (
                <Tag color={isFullySigned ? 'green' : 'orange'} style={{ marginLeft: 4 }}>
                  {item.confirmations ?? 0}/{item.confirmationsRequired} signed
                </Tag>
              )}
            </Typography.Text>
            <Typography.Text type="secondary" copyable={{ text: item.safeTxHash }}>
              {formatAddress(item.safeTxHash, HASH_DISPLAY_CHARS)}
            </Typography.Text>
          </Space>
        </List.Item>
      )
    }}
  />
)

const ViewingContent = ({
  loading,
  batches,
  onClose,
}: {
  loading: boolean
  batches: BatchListItem[]
  onClose: () => void
}) => (
  <>
    {loading ? (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        <p style={{ marginTop: 16 }}>Loading pending transactions from Safe...</p>
      </div>
    ) : batches.length === 0 ? (
      <Empty description="No pending vesting import batches" style={{ padding: '24px 0' }} />
    ) : (
      <>
        <Alert
          type="info"
          showIcon
          message={`${batches.length} vesting import batch(es) awaiting multisig execution`}
          description="Multisig owners must review and execute these transactions in the Safe wallet."
          style={{ marginBottom: 16 }}
        />
        <BatchList items={batches} />
      </>
    )}
    <div style={{ textAlign: 'right' }}>
      <Button onClick={onClose}>Close</Button>
    </div>
  </>
)

const ProposingContent = ({
  currentBatch,
  totalBatches,
  processed,
  total,
}: {
  currentBatch: number
  totalBatches: number
  processed: number
  total: number
}) => (
  <>
    <div className="spin-stake">
      <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 60 }} spin />} />
    </div>
    <p className="modal-text1">
      Submitting batch {currentBatch}/{totalBatches} to Safe multisig. Please wait...
    </p>
    <Progress
      percent={total > 0 ? Math.round((processed / total) * 100) : 0}
      status="active"
      strokeWidth={10}
    />
    <div className="modal-text2">
      <div>
        Processing: {processed}/{total} entries
      </div>
    </div>
  </>
)

const DoneContent = ({ batches, onDone }: { batches: BatchListItem[]; onDone: () => void }) => (
  <>
    <Alert
      type="success"
      showIcon
      icon={<CheckCircleFilled />}
      message={`All ${batches.length} batch(es) have been submitted to Safe multisig.`}
      description="Safe owners must now review and execute each transaction. Save the links below to track execution status."
      style={{ marginBottom: 16 }}
    />
    <BatchList items={batches} />
    <div style={{ textAlign: 'right' }}>
      <Button type="primary" onClick={onDone}>
        Done
      </Button>
    </div>
  </>
)

const ErrorContent = ({
  proposedBatches,
  failedBatch,
  loading,
  onClose,
  onRetry,
}: {
  proposedBatches: BatchListItem[]
  failedBatch: { batchIndex: number; entriesCount: number; message: string }
  loading: boolean
  onClose: () => void
  onRetry: () => void
}) => (
  <>
    {proposedBatches.length > 0 && (
      <>
        <Alert
          type="info"
          showIcon
          message={`${proposedBatches.length} batch(es) submitted successfully`}
          style={{ marginBottom: 12 }}
        />
        <BatchList items={proposedBatches} />
      </>
    )}
    <Alert
      type="error"
      showIcon
      icon={<CloseCircleFilled />}
      message={`Batch #${failedBatch.batchIndex + 1} failed (${failedBatch.entriesCount} entries)`}
      description={failedBatch.message}
      style={{ marginBottom: 16 }}
    />
    <div style={{ textAlign: 'right' }}>
      <Space>
        <Button onClick={onClose}>Close</Button>
        <Button type="primary" loading={loading} onClick={onRetry}>
          Retry remaining
        </Button>
      </Space>
    </div>
  </>
)

interface IButtonImportProps {
  contract: any
  disable?: boolean
  onAfterImport: () => void
}

const ButtonImport = ({ disable, contract, onAfterImport }: IButtonImportProps) => {
  const { type } = useVestingContextV2()
  const { handleConnect } = useWeb3ModalHook()

  const {
    importWhitelist,
    retry,
    close,
    viewPending,
    loading,
    open,
    processed,
    total,
    proposedBatches,
    failedBatch,
    viewingPending,
    pendingBatches,
    fetchingPending,
  } = useImportVestingMultisig({
    type,
    vestingContractAddress: contract?.address as `0x${string}` | undefined,
  })

  const mode: ModalMode = useMemo(() => {
    if (viewingPending) return 'viewing'
    if (failedBatch) return 'error'
    if (loading) return 'proposing'
    if (total > 0 && processed >= total) return 'done'
    return null
  }, [viewingPending, failedBatch, loading, total, processed])

  const isProposing = mode === 'proposing'
  const totalBatches = Math.ceil(total / BATCH_SIZE)

  const handleClick = useCallback(() => {
    if (contract?.address) importWhitelist()
    else handleConnect()
  }, [contract?.address, importWhitelist, handleConnect])

  const handleDone = useCallback(() => {
    const wasViewing = viewingPending
    close()
    if (!wasViewing) onAfterImport()
  }, [viewingPending, close, onAfterImport])

  return (
    <>
      <Space>
        <Button
          type="primary"
          style={{ width: IMPORT_BUTTON_WIDTH }}
          disabled={loading || disable}
          onClick={handleClick}
          size="large"
          icon={loading ? undefined : <UploadOutlined />}
        >
          {loading ? <Spin /> : 'Import Whitelist to contract'}
        </Button>
        <Button
          size="large"
          icon={<EyeOutlined />}
          disabled={loading || disable}
          onClick={viewPending}
        >
          View Pending Batches
        </Button>
      </Space>

      <Modal
        title={mode && mode !== 'proposing' ? MODAL_TITLES[mode] : false}
        open={open}
        footer={false}
        wrapClassName="modal-confirm"
        centered
        closable={!isProposing}
        maskClosable={!isProposing}
        onCancel={isProposing ? undefined : handleDone}
        width={MODAL_WIDTH}
      >
        {mode === 'viewing' && (
          <ViewingContent loading={fetchingPending} batches={pendingBatches} onClose={handleDone} />
        )}
        {mode === 'proposing' && (
          <ProposingContent
            currentBatch={proposedBatches.length + 1}
            totalBatches={totalBatches}
            processed={processed}
            total={total}
          />
        )}
        {mode === 'done' && <DoneContent batches={proposedBatches} onDone={handleDone} />}
        {mode === 'error' && failedBatch && (
          <ErrorContent
            proposedBatches={proposedBatches}
            failedBatch={failedBatch}
            loading={loading}
            onClose={handleDone}
            onRetry={retry}
          />
        )}
      </Modal>
    </>
  )
}

export default ButtonImport
